import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Search, User, Menu, X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import Logo from './Logo'
import { t } from '../utils/translations'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { isAuthenticated, user, logout } = useAuth()
  const { itemCount } = useCart()
  const navigate = useNavigate()

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`)
      setSearchQuery('')
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 shadow-lg bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 space-x-reverse">
            <Logo variant="header" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8 space-x-reverse">
            <Link to="/" className="text-primary-800 hover:text-primary-900 transition-colors font-medium">
              {t('home')}
            </Link>
            <Link to="/products" className="text-primary-800 hover:text-primary-900 transition-colors font-medium">
              {t('products')}
            </Link>
            <Link to="/categories" className="text-primary-800 hover:text-primary-900 transition-colors font-medium">
              الفئات
            </Link>
            {isAuthenticated && user?.isAdmin && (
              <Link to="/admin" className="text-primary-800 hover:text-primary-900 transition-colors font-medium">
                {t('admin')}
              </Link>
            )}
          </div>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <form onSubmit={handleSearch} className="relative w-full">
              <input
                type="text"
                placeholder={t('search') + '...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-primary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-600 transition-all duration-200 text-primary-800 placeholder-primary-400"
              />
              <Search className="absolute right-3 top-2.5 h-5 w-5 text-primary-600" />
            </form>
          </div>

          {/* Right side - Cart and Auth */}
          <div className="flex items-center space-x-4 space-x-reverse">
            {/* Cart */}
            <Link to="/cart" className="relative p-2 text-primary-800 hover:text-primary-900 transition-colors">
              <ShoppingCart className="h-6 w-6" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Auth */}
            {isAuthenticated ? (
              <div className="relative group">
                <button className="flex items-center space-x-2 space-x-reverse text-primary-800 hover:text-primary-900 transition-colors">
                  <User className="h-6 w-6" />
                  <span className="hidden sm:block">{user?.username}</span>
                </button>
                <div className="absolute left-0 top-8 w-48 bg-white rounded-md shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link to="/profile" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                    {t('profile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-right px-4 py-2 text-gray-700 hover:bg-gray-100"
                  >
                    {t('logout')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="hidden md:flex items-center space-x-2 space-x-reverse">
                <Link to="/login" className="btn btn-outline btn-sm">
                  {t('login')}
                </Link>
                <Link to="/register" className="btn btn-primary btn-sm">
                  {t('register')}
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-primary-800 hover:text-primary-900 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-2 space-y-2">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder={t('search') + '...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-primary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-600 transition-all duration-200 text-primary-800 placeholder-primary-400"
              />
              <Search className="absolute right-3 top-2.5 h-5 w-5 text-primary-600" />
            </form>

            {/* Mobile Navigation */}
            <div className="space-y-2">
              <Link
                to="/"
                className="block py-2 text-gray-700 hover:text-primary-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('home')}
              </Link>
              <Link
                to="/products"
                className="block py-2 text-gray-700 hover:text-primary-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('products')}
              </Link>
              <Link
                to="/categories"
                className="block py-2 text-gray-700 hover:text-primary-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                الفئات
              </Link>
              {isAuthenticated && user?.isAdmin && (
                <Link
                  to="/admin"
                  className="block py-2 text-gray-700 hover:text-primary-600 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('admin')}
                </Link>
              )}
            </div>

            {/* Mobile Auth */}
            {!isAuthenticated && (
              <div className="flex space-x-2 space-x-reverse pt-2">
                <Link to="/login" className="btn btn-outline btn-sm flex-1 text-center">
                  {t('login')}
                </Link>
                <Link to="/register" className="btn btn-primary btn-sm flex-1 text-center">
                  {t('register')}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar
